import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import AppLayout from 'components/layout/layout'
import Spinner from 'components/Spinner'
import { CaretDown } from '@phosphor-icons/react'
import ReturnBreadCrumbs from 'components/breadCrumb/NewBreadCrumbs'
import AllMessages from 'pages/Events/Chats/Sections/AllMessages'
import MessageContainer from 'pages/Events/Chats/Sections/MessageContainer'
import RecepientBox from 'components/global/chat/RecepientBox'
import { useGetLeagueById } from 'api/league/LeagueCreation/leagueCreation'
import { useGetLeaguePlayers } from 'api/league/LeagueManage/leagueManage'
import { useGetLeagueAssignedMentors } from 'api/league/LeagueManage/leagueManage'
import { useGetAdminDetails } from 'api/Overview'
import { useFindOrCreateChatRoom } from 'helper/Hooks/chatMessages'
import { eventShatStore } from 'state/event/eventChat'
import { leagueEventStore } from 'state/league/leagueEvent'
import { Person } from 'types/globalTypes'
import LeagueChatModal from './LeagueChatModal'
import 'pages/Events/Chats/styles.scss'

const LeagueChats: React.FC = () => {
  const { id }: { id: string } = useParams()
  const [showReceipientContainer, setShowReceipientContainer] = useState(true)

  const { storeLeagueData, setLeagueData } = leagueEventStore(store => ({
    storeLeagueData: store.leagueData,
    setLeagueData: store.setLeagueData,
  }))

  const {
    chatModalVisible,
    activeChatRoom,
    setActiveChatRoom,
    setSelectedChatRoomMembers,
    setChatModalVisible,
    setSelectedParticipants,
  } = eventShatStore(store => ({
    chatModalVisible: store.chatModalVisible,
    activeChatRoom: store.selectedChatRoomId,
    setActiveChatRoom: store.setSelectedChatRoomId,
    setSelectedChatRoomMembers: store.setSelectedChatRoomMembers,
    setChatModalVisible: store.setChatModalVisible,
    setSelectedParticipants: store.setSelectedParticipants,
  }))

  const {
    data: fetchedLeague,
    isLoading: leagueLoading,
    refetch: refetchLeague,
  } = useGetLeagueById(id)

  useEffect(() => {
    if (!storeLeagueData) refetchLeague()
  }, [id])

  useEffect(() => {
    if (fetchedLeague && !storeLeagueData) setLeagueData(fetchedLeague)
  }, [fetchedLeague])

  // Real MongoDB _id — URL param is commonLeagueId, not _id
  const leagueData = storeLeagueData || fetchedLeague
  const realLeagueId: string = (leagueData as any)?._id || ''

  const chatRoomIds = useFindOrCreateChatRoom(realLeagueId)

  // useGetLeaguePlayers returns ALL registered participants (no date filter)
  const {
    data: playersData,
    isLoading: playersLoading,
    isFetching: playersFetching,
    refetch: refetchPlayers,
  } = useGetLeaguePlayers(realLeagueId)

  const {
    data: mentorsData,
    isLoading: mentorsLoading,
    refetch: refetchMentors,
  } = useGetLeagueAssignedMentors(realLeagueId)

  const {
    data: adminData,
    isLoading: adminLoading,
    isFetching: adminFetching,
    refetch: refetchAdmin,
  } = useGetAdminDetails()

  useEffect(() => {
    refetchAdmin()
  }, [])

  useEffect(() => {
    if (realLeagueId) {
      refetchPlayers()
      refetchMentors()
    }
  }, [realLeagueId])

  useEffect(() => {
    setActiveChatRoom(chatRoomIds && chatRoomIds[0]?.id)
    setSelectedChatRoomMembers(chatRoomIds && chatRoomIds[0]?.data.memberIds)
  }, [chatRoomIds])

  const toPersonList = (
    list: { _id: string; firstName: string; lastName: string; gender?: string; dob?: string }[],
    role: string,
  ): Person[] =>
    list.map(p => ({
      _id: p._id,
      firstName: p.firstName,
      lastName: p.lastName,
      gender: (p.gender || 'male') as 'male' | 'female',
      dob: p.dob || '',
      photoId: '',
      photo: null,
      role,
    }))

  const participants = playersData?.participants
  const allParticipants: Person[] = [
    ...toPersonList(participants?.kids || [], 'kid'),
    ...toPersonList(participants?.guardians || [], 'guardian'),
    ...toPersonList(participants?.authorizedAdults || [], 'authorizedAdult'),
    ...(mentorsData || []).map(m => ({
      _id: m._id,
      firstName: m.firstName || m.name || '',
      lastName: m.lastName || '',
      gender: 'male' as 'male',
      dob: '',
      photoId: '',
      photo: null,
      role: 'mentor',
    })),
    ...(adminData ? [{ ...adminData }] : []),
  ]

  const isLoading =
    (!realLeagueId && leagueLoading) || playersLoading || mentorsLoading || adminLoading || adminFetching

  return (
    <AppLayout>
      <div className="container">
        {isLoading ? (
          <div className="px-40px py-40px w-full flex justify-center align-center border-btm">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="px-40px py-40px border-btm">
              <div className="flex justify-between">
                <div>
                  <ReturnBreadCrumbs
                    links={[
                      { name: 'Events', link: '/spaces' },
                      {
                        name: leagueData?.name || 'League',
                        link: `/spaces/league-detail/${id}`,
                      },
                      {
                        name: 'Contact attendees',
                        link: `/league-chat/${id}`,
                      },
                    ]}
                  />
                  <h3 className="mt-15px text-24 w-600">Contact attendees</h3>
                </div>
                <div className="flex align-center gap-20px">
                  <button className="flex align-center justify-center border-none gap-16px bg-dark-gray px-20px h-40px radius-8">
                    <span className="text-14 w-500">More</span>
                    <CaretDown size={20} color="#000" />
                  </button>
                  <button
                    className="flex align-center justify-center border-none gap-16px bg-blue px-20px h-40px radius-8 pointer"
                    onClick={() => {
                      setChatModalVisible(true)
                      setSelectedParticipants([])
                    }}
                  >
                    <span className="text-14 w-500 text-white">New Chat</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex bg-white h-full">
              {playersFetching ? (
                <div className="py-40px w-full flex justify-center align-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className="all-chats-name">
                    <AllMessages
                      chatRoomIds={chatRoomIds || []}
                      participants={allParticipants}
                      activeChatRoom={activeChatRoom}
                      setActiveChatRoom={setActiveChatRoom}
                    />
                  </div>

                  <div
                    className={`message-container px-20px ${
                      showReceipientContainer ? 'recipient' : 'recipient-hidden'
                    }`}
                  >
                    <MessageContainer
                      showReciepientContainer={showReceipientContainer}
                      setShowReceipientContainer={setShowReceipientContainer}
                      activeChatRoomId={activeChatRoom}
                      me={adminData}
                      participants={allParticipants}
                    />
                  </div>

                  <div
                    className={`recepient-container scroller overflow-auto-y ${
                      showReceipientContainer ? '' : 'hidden'
                    }`}
                  >
                    {allParticipants.map(item => (
                      <RecepientBox
                        key={item._id}
                        avatar={item.photo?.post}
                        name={item.firstName}
                        role={
                          item.role as
                            | 'mentor'
                            | 'admin'
                            | 'guardian'
                            | 'adult'
                            | 'kid'
                            | 'organizer'
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {chatModalVisible && (
        <LeagueChatModal leagueId={realLeagueId} participants={allParticipants} />
      )}
    </AppLayout>
  )
}

export default LeagueChats
